import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Search, X, Loader2, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 100;

const SeasonalPackingReport = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalQty, setTotalQty] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchReport = useCallback(async (pageArg = 1, overrides = {}) => {
    const searchArg = overrides.search !== undefined ? overrides.search : search;
    const fromArg = overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
    const toArg = overrides.dateTo !== undefined ? overrides.dateTo : dateTo;
    setLoading(true);
    try {
      const r = await apiClient.get('/wms/seasonal/packing-report', {
        params: {
          q: searchArg || undefined,
          date_from: fromArg || undefined,
          date_to: toArg || undefined,
          page: pageArg,
          page_size: PAGE_SIZE,
        },
      });
      const d = r.data || {};
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setTotalQty(d.total_qty || 0);
      setPage(d.page || pageArg);
      setTotalPages(d.total_pages || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setRows([]);
      setTotal(0);
      setTotalQty(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo]);

  useEffect(() => { fetchReport(1, { search: '', dateFrom: '', dateTo: '' }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => fetchReport(1);
  const handleClear = () => {
    setSearch(''); setDateFrom(''); setDateTo('');
    fetchReport(1, { search: '', dateFrom: '', dateTo: '' });
  };
  const goPage = (p) => { if (p >= 1 && p <= totalPages) fetchReport(p); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await apiClient.get('/wms/seasonal/packing-report', {
        params: {
          q: search || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          format: 'csv',
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `seasonal_packing_report_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stat = (label, value, color) => (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
      <p className="text-xs text-[#5A5F6E]">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color || 'text-[#1A1F2E]'}`}>{value}</p>
    </div>
  );

  const cell = (v) => (v || v === 0 ? v : <span className="text-gray-300">—</span>);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-packing-report-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/fg')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="spr-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <ClipboardList className="w-7 h-7 mr-3" />
          <div>
            <h1 className="text-xl font-bold font-display">Seasonal Packing Report</h1>
            <p className="text-sm text-[#E8DCC8]">Per box · per barcode — all packed seasonal boxes</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="pt-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Global filter</label>
              <div className="flex gap-2">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                  placeholder="Search slug, customer, SP number, box, barcode, design, shade, size, description, DC…"
                  className="flex-1"
                  data-testid="spr-search-input"
                />
                <Button onClick={handleSearch} className="bg-lime-600 hover:bg-lime-700 text-white" data-testid="spr-search-btn">
                  <Search className="w-4 h-4" />
                </Button>
                <Button onClick={handleClear} variant="outline" className="border-[#E5DFD0]" data-testid="spr-clear-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Packed from (IST)</label>
                <Input type="datetime-local" value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)} data-testid="spr-date-from" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-[#5A5F6E] mb-1">Packed to (IST)</label>
                <Input type="datetime-local" value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)} data-testid="spr-date-to" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSearch} variant="outline" className="border-[#E5DFD0]" data-testid="spr-apply-dates-btn">
                  Apply
                </Button>
                <Button onClick={handleExport} disabled={exporting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="spr-export-csv-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div data-testid="spr-summary-total">{stat('Rows', total, 'text-indigo-700')}</div>
          <div data-testid="spr-summary-qty">{stat('Total Box Qty', totalQty, 'text-amber-700')}</div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="spr-report-table">
                <thead>
                  <tr className="text-left text-[#5A5F6E] bg-gray-50 border-b whitespace-nowrap">
                    <th className="px-3 py-2.5 font-medium">Slug Number</th>
                    <th className="px-3 py-2.5 font-medium">Customer Name</th>
                    <th className="px-3 py-2.5 font-medium">Pick Slip #</th>
                    <th className="px-3 py-2.5 font-medium">Packed Box #</th>
                    <th className="px-3 py-2.5 font-medium">Barcode</th>
                    <th className="px-3 py-2.5 font-medium">Design Name</th>
                    <th className="px-3 py-2.5 font-medium">Shade</th>
                    <th className="px-3 py-2.5 font-medium">Size</th>
                    <th className="px-3 py-2.5 font-medium">Description</th>
                    <th className="px-3 py-2.5 font-medium text-right">Box Qty</th>
                    <th className="px-3 py-2.5 font-medium">Packed At (IST)</th>
                    <th className="px-3 py-2.5 font-medium">DC Tagged</th>
                    <th className="px-3 py-2.5 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={13} className="px-3 py-10 text-center text-[#5A5F6E]">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                    </td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={13} className="px-3 py-10 text-center text-[#5A5F6E]" data-testid="spr-empty">
                      No packed seasonal boxes found.
                    </td></tr>
                  ) : rows.map((r, i) => (
                    <tr key={`${r.box_number}-${r.barcode}-${i}`} className={`border-b hover:bg-gray-50 ${r.cancelled ? 'bg-gray-50/60 text-gray-400' : ''}`} data-testid={`spr-row-${i}`}>
                      <td className="px-3 py-2 font-mono">{cell(r.slug)}</td>
                      <td className="px-3 py-2">{cell(r.customer)}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-lime-700">{cell(r.pick_slip_number)}</td>
                      <td className="px-3 py-2 font-mono">{cell(r.box_number)}</td>
                      <td className="px-3 py-2 font-mono">{cell(r.barcode)}</td>
                      <td className="px-3 py-2">{cell(r.design_name)}</td>
                      <td className="px-3 py-2">{cell(r.shade)}</td>
                      <td className="px-3 py-2">{cell(r.size)}</td>
                      <td className="px-3 py-2">{cell(r.description)}</td>
                      <td className="px-3 py-2 text-right font-semibold">{r.qty}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-[#5A5F6E]" data-testid={`spr-packed-at-${i}`}>{cell(r.packed_at_ist)}</td>
                      <td className="px-3 py-2 whitespace-nowrap" data-testid={`spr-dc-${i}`}>
                        {r.dc_number ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px] font-mono">{r.dc_number}</Badge>
                        ) : r.dc_tagged ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[11px]">Tagged</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px]">Pending</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap" data-testid={`spr-status-${i}`}>
                        {r.cancelled ? (
                          <Badge className="bg-gray-200 text-gray-700 border-0 text-[11px]" title={r.cancel_reason || 'Cancelled'}>Cancelled</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700 border-0 text-[11px]">Packed</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" onClick={() => goPage(page - 1)} disabled={page <= 1} data-testid="spr-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-[#5A5F6E]" data-testid="spr-page-info">Page {page} of {totalPages}</span>
            <Button variant="outline" onClick={() => goPage(page + 1)} disabled={page >= totalPages} data-testid="spr-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default SeasonalPackingReport;
